import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology } from 'topojson-specification';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import type { ClusterDetail, StateDetailSchool, StateDetailTerritory } from '../../types';
import { MAJOR_CITIES } from '../../constants/majorCities';
import SchoolTooltip from './SchoolTooltip';
import TerritoryHoverCard from './TerritoryHoverCard';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const FIPS_TO_STATE: Record<string, string> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA',
  '08': 'CO', '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL',
  '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN',
  '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME',
  '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
  '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
  '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI',
  '45': 'SC', '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT',
  '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV', '55': 'WI',
  '56': 'WY',
};

const SCHOOL_COLORS = {
  blast: '#22c55e',
  competitor: '#ef4444',
  open: '#9ca3af',
} as const;

const TERRITORY_COLORS = [
  '#6366f1', // indigo
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
  '#14b8a6', // teal
  '#84cc16', // lime
  '#a855f7', // purple
  '#e11d48', // rose
];

interface ClusterMapProps {
  cluster: { centerLat: number; centerLng: number; radiusMiles: number };
  clusterDetail: ClusterDetail;
  zctaData: GeoJSON.FeatureCollection | null;
  showTerritories: boolean;
  showHighways: boolean;
  showCities: boolean;
}

interface TooltipState {
  type: 'school' | 'territory';
  x: number;
  y: number;
  school?: StateDetailSchool;
  territory?: StateDetailTerritory;
}

const MAP_WIDTH = 800;
const MAP_HEIGHT = 600;
const MAP_PADDING = 40;
const MIN_ZOOM = 1;
const MAX_ZOOM = 40;
const ZOOM_STEP = 1.4;

export default function ClusterMap({ cluster, clusterDetail, zctaData, showTerritories, showHighways, showCities }: ClusterMapProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [stateGeos, setStateGeos] = useState<any[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Zoom & pan state
  const [zoom, setZoom] = useState(1);
  const [viewOrigin, setViewOrigin] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ clientX: 0, clientY: 0, originX: 0, originY: 0 });
  const tooltipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load state outlines from US Atlas — filter to states in this cluster
  useEffect(() => {
    let cancelled = false;
    fetch(GEO_URL)
      .then(r => r.json())
      .then((topo: Topology) => {
        if (cancelled) return;
        const allStates = feature(topo, topo.objects.states) as GeoJSON.FeatureCollection;
        const matching = allStates.features.filter((f: any) => {
          const fips = f.id?.toString().padStart(2, '0');
          const stateCode = FIPS_TO_STATE[fips];
          return stateCode && clusterDetail.states.includes(stateCode);
        });
        setStateGeos(matching);
      })
      .catch(err => console.error('Failed to load state outlines:', err));
    return () => { cancelled = true; };
  }, [clusterDetail.states]);

  // Load highway data
  const [highwayData, setHighwayData] = useState<GeoJSON.FeatureCollection | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/data/highways.json')
      .then(r => r.json())
      .then((data: GeoJSON.FeatureCollection) => {
        if (cancelled) return;
        setHighwayData(data);
      })
      .catch(() => {}); // highways are optional
    return () => { cancelled = true; };
  }, []);

  // Cities filtered by cluster bounding box (with 50% margin to catch nearby cities)
  const cities = useMemo(() => {
    const margin = 1.5;
    const degreesLat = (cluster.radiusMiles / 69) * margin;
    const degreesLng = (cluster.radiusMiles / (69 * Math.cos(cluster.centerLat * Math.PI / 180))) * margin;
    return MAJOR_CITIES.filter(c =>
      c.lat >= cluster.centerLat - degreesLat &&
      c.lat <= cluster.centerLat + degreesLat &&
      c.lng >= cluster.centerLng - degreesLng &&
      c.lng <= cluster.centerLng + degreesLng
    );
  }, [cluster.centerLat, cluster.centerLng, cluster.radiusMiles]);

  // Build projection fitted to cluster center ± radius
  const projection = useMemo(() => {
    const degreesLat = cluster.radiusMiles / 69;
    const degreesLng = cluster.radiusMiles / (69 * Math.cos(cluster.centerLat * Math.PI / 180));

    const proj = geoMercator();
    proj.fitExtent(
      [[MAP_PADDING, MAP_PADDING], [MAP_WIDTH - MAP_PADDING, MAP_HEIGHT - MAP_PADDING]],
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [cluster.centerLng - degreesLng, cluster.centerLat - degreesLat],
            [cluster.centerLng - degreesLng, cluster.centerLat + degreesLat],
            [cluster.centerLng + degreesLng, cluster.centerLat + degreesLat],
            [cluster.centerLng + degreesLng, cluster.centerLat - degreesLat],
            [cluster.centerLng - degreesLng, cluster.centerLat - degreesLat],
          ]],
        },
      }
    );
    return proj;
  }, [cluster.centerLat, cluster.centerLng, cluster.radiusMiles]);

  const pathGen = useMemo(() => {
    if (!projection) return null;
    return geoPath().projection(projection);
  }, [projection]);

  // Build a prefix → territory lookup for coloring ZCTA zones
  const prefixToTerritory = useMemo(() => {
    const map: Record<string, { territory: StateDetailTerritory; colorIndex: number }> = {};
    clusterDetail.territories.forEach((t, i) => {
      t.zipPrefixes.forEach(prefix => {
        map[prefix] = { territory: t, colorIndex: i };
      });
    });
    return map;
  }, [clusterDetail.territories]);

  // Assign every ZCTA feature to a territory
  const zctaTerritoryMap = useMemo(() => {
    if (!zctaData) return new Map<number, { territory: StateDetailTerritory; color: string }>();

    const centroid = (geom: any): [number, number] => {
      const pts: number[][] = [];
      const collect = (coords: any) => {
        if (typeof coords[0] === 'number') { pts.push(coords); return; }
        for (const c of coords) collect(c);
      };
      collect(geom.coordinates);
      if (pts.length === 0) return [0, 0];
      const sx = pts.reduce((s, p) => s + p[0], 0);
      const sy = pts.reduce((s, p) => s + p[1], 0);
      return [sx / pts.length, sy / pts.length];
    };

    // Pre-compute territory centroids
    const territoryCentroids: [number, number][] = clusterDetail.territories.map((t) => {
      const prefixes = new Set(t.zipPrefixes);
      const tCentroids: [number, number][] = [];
      zctaData.features.forEach(f => {
        if (f.properties?.prefix && prefixes.has(f.properties.prefix)) {
          tCentroids.push(centroid(f.geometry));
        }
      });
      if (tCentroids.length === 0) return [0, 0] as [number, number];
      return [
        tCentroids.reduce((s, c) => s + c[0], 0) / tCentroids.length,
        tCentroids.reduce((s, c) => s + c[1], 0) / tCentroids.length,
      ] as [number, number];
    });

    const result = new Map<number, { territory: StateDetailTerritory; color: string }>();

    zctaData.features.forEach((f, i) => {
      const prefix = f.properties?.prefix;

      // Check if directly assigned
      if (prefix && prefixToTerritory[prefix]) {
        const match = prefixToTerritory[prefix];
        result.set(i, {
          territory: match.territory,
          color: TERRITORY_COLORS[match.colorIndex % TERRITORY_COLORS.length],
        });
        return;
      }

      // Unassigned — find geographically nearest territory
      const c = centroid(f.geometry);
      let bestIdx = -1;
      let bestDist = Infinity;
      territoryCentroids.forEach((tc, tIdx) => {
        const dx = c[0] - tc[0];
        const dy = c[1] - tc[1];
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = tIdx;
        }
      });

      if (bestIdx >= 0) {
        result.set(i, {
          territory: clusterDetail.territories[bestIdx],
          color: TERRITORY_COLORS[bestIdx % TERRITORY_COLORS.length],
        });
      }
    });

    return result;
  }, [zctaData, clusterDetail.territories, prefixToTerritory]);

  // Convert screen pixel position to viewBox coordinates
  const screenToViewBox = useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const vbWidth = MAP_WIDTH / zoom;
    const vbHeight = MAP_HEIGHT / zoom;
    return {
      x: viewOrigin.x + (clientX - rect.left) / rect.width * vbWidth,
      y: viewOrigin.y + (clientY - rect.top) / rect.height * vbHeight,
    };
  }, [zoom, viewOrigin]);

  // Zoom centered on a viewBox point
  const zoomAt = useCallback((factor: number, clientX?: number, clientY?: number) => {
    setZoom(prev => {
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev * factor));
      const actualFactor = newZoom / prev;
      if (actualFactor === 1) return prev;

      if (clientX !== undefined && clientY !== undefined) {
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
          const oldVbW = MAP_WIDTH / prev;
          const oldVbH = MAP_HEIGHT / prev;
          const fracX = (clientX - rect.left) / rect.width;
          const fracY = (clientY - rect.top) / rect.height;
          const cursorVbX = viewOrigin.x + fracX * oldVbW;
          const cursorVbY = viewOrigin.y + fracY * oldVbH;

          const newVbW = MAP_WIDTH / newZoom;
          const newVbH = MAP_HEIGHT / newZoom;

          setViewOrigin({
            x: cursorVbX - fracX * newVbW,
            y: cursorVbY - fracY * newVbH,
          });
        }
      } else {
        const oldVbW = MAP_WIDTH / prev;
        const oldVbH = MAP_HEIGHT / prev;
        const newVbW = MAP_WIDTH / newZoom;
        const newVbH = MAP_HEIGHT / newZoom;
        setViewOrigin(o => ({
          x: o.x + (oldVbW - newVbW) / 2,
          y: o.y + (oldVbH - newVbH) / 2,
        }));
      }

      return newZoom;
    });
  }, [viewOrigin]);

  const resetView = useCallback(() => {
    setZoom(1);
    setViewOrigin({ x: 0, y: 0 });
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
    zoomAt(factor, e.clientX, e.clientY);
  }, [zoomAt]);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStart.current = { clientX: e.clientX, clientY: e.clientY, originX: viewOrigin.x, originY: viewOrigin.y };
  }, [viewOrigin]);

  const handleMouseMoveGlobal = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const vbWidth = MAP_WIDTH / zoom;
    const vbHeight = MAP_HEIGHT / zoom;
    const dx = ((e.clientX - panStart.current.clientX) / rect.width) * vbWidth;
    const dy = ((e.clientY - panStart.current.clientY) / rect.height) * vbHeight;
    setViewOrigin({ x: panStart.current.originX - dx, y: panStart.current.originY - dy });
  }, [isPanning, zoom]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Tooltip with debounce
  const getRelativePos = useCallback((e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    return {
      x: e.clientX - (rect?.left ?? 0),
      y: e.clientY - (rect?.top ?? 0),
    };
  }, []);

  const showTooltip = useCallback((state: TooltipState) => {
    if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    setTooltip(state);
  }, []);

  const handleSchoolHover = useCallback((e: React.MouseEvent, school: StateDetailSchool) => {
    if (isPanning) return;
    const pos = getRelativePos(e);
    showTooltip({ type: 'school', ...pos, school });
  }, [getRelativePos, showTooltip, isPanning]);

  const handleZoneHover = useCallback((e: React.MouseEvent, territory: StateDetailTerritory) => {
    if (isPanning) return;
    const pos = getRelativePos(e);
    showTooltip({ type: 'territory', ...pos, territory });
  }, [getRelativePos, showTooltip, isPanning]);

  const handleTooltipMove = useCallback((e: React.MouseEvent) => {
    if (!tooltip || isPanning) return;
    const pos = getRelativePos(e);
    setTooltip(prev => prev ? { ...prev, ...pos } : null);
  }, [tooltip, getRelativePos, isPanning]);

  const handleMouseLeave = useCallback(() => {
    if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    tooltipTimer.current = setTimeout(() => setTooltip(null), 50);
  }, []);

  if (!projection || !pathGen) {
    return <div className="h-96 flex items-center justify-center text-gray-500 dark:text-gray-400">Unable to render map</div>;
  }

  // Compute viewBox based on zoom + viewOrigin
  const vbWidth = MAP_WIDTH / zoom;
  const vbHeight = MAP_HEIGHT / zoom;
  const vbX = viewOrigin.x;
  const vbY = viewOrigin.y;

  // Scale dot size inversely with zoom
  const dotRadius = 3 / zoom;
  const dotStroke = 0.5 / zoom;

  // Suppress unused screenToViewBox warning — kept for parity with StateMap
  void screenToViewBox;

  return (
    <div ref={containerRef} className="relative bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
      {/* Zoom controls */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
        <button
          onClick={() => zoomAt(ZOOM_STEP, MAP_WIDTH / 2, MAP_HEIGHT / 2)}
          className="p-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          title="Zoom in"
        >
          <ZoomIn className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </button>
        <button
          onClick={() => zoomAt(1 / ZOOM_STEP, MAP_WIDTH / 2, MAP_HEIGHT / 2)}
          className="p-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          title="Zoom out"
        >
          <ZoomOut className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </button>
        <button
          onClick={resetView}
          className="p-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          title="Reset view"
        >
          <Maximize2 className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </button>
        {zoom > 1 && (
          <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {zoom.toFixed(1)}x
          </div>
        )}
      </div>

      <svg
        ref={svgRef}
        viewBox={`${vbX} ${vbY} ${vbWidth} ${vbHeight}`}
        className="w-full h-auto select-none"
        style={{ maxHeight: '70vh', cursor: isPanning ? 'grabbing' : 'grab' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={(e) => { handleMouseMoveGlobal(e); handleTooltipMove(e); }}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { handleMouseUp(); handleMouseLeave(); }}
      >
        {/* Clip path for state boundaries — clips highways to within the states */}
        {stateGeos.length > 0 && (
          <defs>
            <clipPath id="cluster-states-clip">
              {stateGeos.map((geo, i) => (
                <path key={`clip-${i}`} d={pathGen(geo) ?? ''} />
              ))}
            </clipPath>
          </defs>
        )}

        {/* Layer 1: State outlines background (all matching states) */}
        {stateGeos.map((geo, i) => (
          <path
            key={`state-${i}`}
            d={pathGen(geo) ?? ''}
            strokeWidth={1.5 / zoom}
            className="fill-gray-100 stroke-gray-300 dark:fill-gray-700 dark:stroke-gray-600"
          />
        ))}

        {/* Layer 2a: Territory color fills (toggleable) */}
        {showTerritories && zctaData && zctaData.features.map((feature, i) => {
          const match = zctaTerritoryMap.get(i);
          if (!match) return null;
          return (
            <path
              key={`zcta-fill-${i}`}
              d={pathGen!(feature as any) ?? ''}
              fill={match.color}
              fillOpacity={0.25}
              stroke="none"
              onMouseEnter={(e) => handleZoneHover(e, match.territory)}
              onMouseMove={handleTooltipMove}
              onMouseLeave={handleMouseLeave}
              style={{ cursor: 'pointer' }}
            />
          );
        })}

        {/* Layer 2b: Faint ZIP hairlines (always visible) */}
        {zctaData && zctaData.features.map((feature, i) => (
          <path
            key={`zcta-line-${i}`}
            d={pathGen!(feature as any) ?? ''}
            fill="none"
            stroke="white"
            strokeWidth={0.3 / zoom}
            strokeOpacity={0.15}
            pointerEvents="none"
          />
        ))}

        {/* Cluster boundary circle (between ZIP hairlines and highways) */}
        {projection && (() => {
          const center = projection([cluster.centerLng, cluster.centerLat]);
          if (!center) return null;
          const edgePoint = projection([
            cluster.centerLng + cluster.radiusMiles / (69 * Math.cos(cluster.centerLat * Math.PI / 180)),
            cluster.centerLat,
          ]);
          if (!edgePoint) return null;
          const pixelRadius = Math.abs(edgePoint[0] - center[0]);
          return (
            <circle
              cx={center[0]}
              cy={center[1]}
              r={pixelRadius}
              fill="none"
              stroke="#9ca3af"
              strokeWidth={1.5 / zoom}
              strokeDasharray={`${4 / zoom} ${4 / zoom}`}
              opacity={0.4}
              pointerEvents="none"
            />
          );
        })()}

        {/* Layer 3: Highways (clipped to states) */}
        {showHighways && highwayData && pathGen && (
          <g clipPath={stateGeos.length > 0 ? 'url(#cluster-states-clip)' : undefined}>
            {highwayData.features.map((feature, i) => {
              const d = pathGen(feature as any);
              if (!d) return null;
              const name = (feature.properties as any)?.name;
              const label = name ? `I-${name}` : '';
              return (
                <g key={`hwy-${i}`}>
                  {/* Highway line */}
                  <path
                    d={d}
                    fill="none"
                    stroke="#fbbf24"
                    strokeWidth={1.8 / zoom}
                    strokeOpacity={0.7}
                    strokeLinecap="round"
                    pointerEvents="none"
                  />
                  {/* Highway label — rendered along the path */}
                  {label && (
                    <>
                      <defs>
                        <path id={`hwy-path-${i}`} d={d} />
                      </defs>
                      <text
                        fontSize={10 / zoom}
                        fill="#fbbf24"
                        fillOpacity={0.9}
                        stroke="#1f2937"
                        strokeWidth={2 / zoom}
                        strokeLinejoin="round"
                        paintOrder="stroke"
                        pointerEvents="none"
                        className="select-none"
                        style={{ fontFamily: 'system-ui, sans-serif', fontWeight: 600 }}
                      >
                        <textPath href={`#hwy-path-${i}`} startOffset="30%">
                          {label}
                        </textPath>
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </g>
        )}

        {/* Layer 4: School dots */}
        {clusterDetail.schools.map(school => {
          const point = projection([school.lng, school.lat]);
          if (!point) return null;
          const [cx, cy] = point;

          return (
            <circle
              key={school.id}
              cx={cx}
              cy={cy}
              r={dotRadius}
              fill={SCHOOL_COLORS[school.status]}
              stroke="#ffffff"
              strokeWidth={dotStroke}
              opacity={0.85}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => { e.stopPropagation(); handleSchoolHover(e, school); }}
              onMouseMove={(e) => { e.stopPropagation(); handleTooltipMove(e); }}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}

        {/* Layer 5: City labels ON TOP of school dots */}
        {showCities && projection && cities.map((city, i) => {
          const point = projection([city.lng, city.lat]);
          if (!point) return null;
          const [cx, cy] = point;
          const fontSize = (city.pop > 500000 ? 16 : city.pop > 200000 ? 13 : 10) / zoom;
          const dotR = 2.5 / zoom;
          const offset = 5 / zoom;
          const strokeW = 3.5 / zoom;
          // Cluster map is already zoomed to a small area — show all cities in the bounding box
          // Only hide very small cities when zoomed out from default
          if (zoom < 0.8 && city.pop < 50000) return null;
          return (
            <g key={`city-${i}`} pointerEvents="none">
              <circle cx={cx} cy={cy} r={dotR} fill="#ffffff" stroke="#374151" strokeWidth={0.5 / zoom} />
              <text
                x={cx + offset}
                y={cy + fontSize * 0.35}
                fontSize={fontSize}
                fill="#e5e7eb"
                stroke="#1f2937"
                strokeWidth={strokeW}
                strokeLinejoin="round"
                paintOrder="stroke"
                className="select-none"
                style={{ fontFamily: 'system-ui, sans-serif', fontWeight: city.pop > 500000 ? 700 : 500 }}
              >
                {city.name}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltips */}
      {tooltip?.type === 'school' && tooltip.school && (
        <SchoolTooltip position={{ x: tooltip.x, y: tooltip.y }} school={tooltip.school} />
      )}
      {tooltip?.type === 'territory' && tooltip.territory && (
        <TerritoryHoverCard position={{ x: tooltip.x, y: tooltip.y }} territory={tooltip.territory} />
      )}

      {/* Zoom hint */}
      {zoom === 1 && (
        <div className="absolute bottom-3 left-3 text-xs text-gray-400 dark:text-gray-500">
          Scroll to zoom, drag to pan
        </div>
      )}
    </div>
  );
}
